import { useState } from "react";
import { useTheme } from "@emotion/react";
import styled from "@emotion/styled";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@radix-ui/react-collapsible";
import { Flex, Text } from "styles/utility-components";

import UIcon from "lib/icons/untitled_ui/UIcon";
import { AiSourceType } from "lib/types/graphQLEnums";
import DocumentSource from "./sources/DocumentSource";
import NoticeSource from "./sources/NoticeSource";
import WebsiteSource from "./sources/WebsiteSource";
import { AiSource } from "./types";

type Props = {
  sources: AiSource[];
};

const AiChatSource = ({ source }: { source: AiSource }) => {
  switch (source.type) {
    case "notice":
      if (!source.id) return <></>;
      return <NoticeSource source={source} />;

    case "document":
      if (!source.id) return <></>;
      return <DocumentSource source={source} />;

    case "website":
      if (!source.url) return <></>;
      return <WebsiteSource source={source} />;
  }
};

export default function AiChatSources({ sources: rawSources }: Props) {
  const { colors } = useTheme();

  const [isOpen, setIsOpen] = useState(false);

  const sources = removeDuplicatesAndAppendLocations(rawSources);

  if (!sources.length) return <></>;

  return (
    <AiSourcesContainer>
      <Collapsible open={isOpen} onOpenChange={setIsOpen}>
        <Trigger asChild>
          <Flex gap={4} alignItems="center">
            <Text color={colors.sysPrimaryDefault}>
              {sources.length} reference{sources.length > 1 && "s"}
            </Text>
            <UIcon icon={isOpen ? "chevronUp" : "chevronDown"} color={colors.sysPrimaryDefault} />
          </Flex>
        </Trigger>
        <Content>
          <Text color={colors.sysTextSecondary}>Viewing results ({sources.length})</Text>
          <SourcesContainer>
            {sources.map((source) => (
              <SourceContainer key={`${source.type}:${source.title}`}>
                <AiChatSource source={source} />
              </SourceContainer>
            ))}
          </SourcesContainer>
        </Content>
      </Collapsible>
    </AiSourcesContainer>
  );
}

function removeDuplicatesAndAppendLocations(sources: AiSource[]): AiSource[] {
  const combinedSources = sources.reduce(
    (map, source) => {
      const key = `${source.type}:${source.title}`;
      const current = map[key];
      if (
        current &&
        current.type === AiSourceType.Document &&
        source.type === AiSourceType.Document
      ) {
        const currentLocations = current.locations || [];
        const sourceLocations = source.locations || [];
        map[key] = {
          ...current,
          locations: [...currentLocations, ...sourceLocations],
        };
      } else {
        map[key] = source;
      }
      return map;
    },
    {} as Record<string, AiSource>,
  );
  return Object.values(combinedSources);
}

const AiSourcesContainer = styled.div({
  paddingLeft: 40,
});

const Trigger = styled(CollapsibleTrigger)({
  cursor: "pointer",
});

const Content = styled(CollapsibleContent)({
  padding: "12px 0",
});

const SourcesContainer = styled(Flex)({
  flexDirection: "column",
  marginTop: 16,
  gap: 8,
});

const SourceContainer = styled.div(({ theme }) => ({
  padding: 8,
  borderRadius: 4,
  border: `1px solid ${theme.colors.sysBorderDecorative}`,
}));
